import { useEffect, useMemo, useRef, useState } from 'react';
import { startCase } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import SegmentedControl from 'components/shared/SegmentedControl';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import AttrValue from 'models/AttrValue';
import Workflow, { ATTACHMENTS_VALUE_NAME, WorkflowAction } from 'models/Workflow';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import {
  createWorkflow,
  updateFacet,
  updateWorkflow,
  workflowQueryKeys,
  WorkflowValuesInput,
} from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall, BodySmallBold } from 'styles/typography';

import {
  DEMOGRAPHIC_FIELDS,
  PROVIDER_FIELDS,
  REFERRAL_INTAKE_API_NAME,
  REFERRAL_LEVEL_MEMBER_ID_FIELD,
  REFERRAL_SOURCE_FIELD,
  REFERRAL_SOURCE_SELECT_STYLES,
  ReferralFieldDef,
  VALUE_NAMES,
} from './constants';
import {
  COVERAGE_SEQUENCES,
  CoverageEntry,
  COVERAGES_VALUE,
  coveragesFromEntries,
  duplicatesOneInsurance,
  emptyCoverageEntries,
} from './coverages';
import PacketEditor, { usePacketEditor } from './PacketEditor';
import { RestartConsentStep } from './RestartConsentModal';
import { isPartialSsn, ssnDigits } from './ssn';
import SsnField from './SsnField';
import { WarningBanner, WarningBannerText } from './WarningBanner';

// Which section the modal opens focused on. 'demographics' expands the demographics
// section (the "Edit demographics" entry point); 'documents' is the default packet view
// (the "Add or remove documents" entry point); 'facilities' edits only which buildings the
// referral was sent to (the "Edit" link on the Referral Sent To card). On create it decides which
// method the modal opens on.
export type ReferralModalSection = 'documents' | 'demographics' | 'facilities';

interface Props {
  // Present in edit mode; absent when creating.
  workflow?: Workflow;
  section?: ReferralModalSection;
  onClose: () => void;
}

type BuildingOption = { id: string; name: string };

// How a new referral is being created (UX, 2026-08-04). The two paths are alternatives, not
// steps: upload a packet and let extraction fill everything in, or type what you know. Only the
// typing path has a second step, because only it asks for insurance.
type CreateMethod = 'upload' | 'manual';

// Every value field across both sections; the date field is handled separately (dob state),
// the rest (text + select) share the `text` record.
// Includes the conditional referral-wide member ID so its stored value is read and diffed like any
// other field; whether it's RENDERED is decided per referral below.
const VALUE_FIELDS = [...DEMOGRAPHIC_FIELDS, ...PROVIDER_FIELDS, REFERRAL_LEVEL_MEMBER_ID_FIELD, REFERRAL_SOURCE_FIELD];
const TEXT_FIELDS = VALUE_FIELDS.filter((field) => field.type !== 'date');

const sameIds = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

export default function ReferralModal({ workflow, section = 'documents', onClose }: Props) {
  const navigate = useNavigate();
  const isEdit = !!workflow;

  const [selectedBuildings, setSelectedBuildings] = useState<BuildingOption[]>(
    workflow?.groups.map((group) => ({ id: group.id, name: group.name })) ?? []
  );

  const stored = useMemo(() => {
    const text = Object.fromEntries(
      TEXT_FIELDS.map((field) => {
        const value = workflow?.valueFor(field.name)?.value ?? '';
        // Normalised on the way in so the saved diff compares digits with digits. A stored value
        // written before this form held to a shape (or by anything writing dashes) would otherwise
        // read as changed the moment the modal opened, rewriting the number and re-running every
        // step that reads it.
        return [field.name, field.type === 'ssn' ? ssnDigits(String(value)) : value];
      })
    );
    const dob = workflow?.valueFor(VALUE_NAMES.PATIENT_DOB)?.value ?? '';
    return { text, dob };
  }, [workflow]);
  const originalGroupIds = useMemo(() => workflow?.groups.map((group) => group.id) ?? [], [workflow]);

  const [text, setText] = useState<Record<string, string>>(stored.text);
  const [dob, setDob] = useState<Date | null>(parseDate(stored.dob) ?? null);
  const packet = usePacketEditor(workflow?.attachments ?? []);
  const { uploads } = packet;
  // Whether this person has typed in the SSN field. The shape rule applies to what they enter, not
  // to what was already on the referral: a packet that prints the number masked (XXX-XX-7554) leaves
  // extraction storing only what it could read, and extraction's answer is stored verbatim. Gating on
  // the stored value would make every one of those referrals refuse to save any demographic edit,
  // over a number the person never touched and may not have.
  const [ssnEdited, setSsnEdited] = useState(false);
  // The impact list awaiting the user's consent (design §9) — non-null while the modal
  // is on its second (confirm) step. Going back clears it without saving; the form's
  // state lives here in the parent, so the person's edits are preserved.
  const [pendingRestart, setPendingRestart] = useState<WorkflowAction[] | null>(null);
  // Create only. Edit stays one form: its second step is already the restart consent, and
  // stacking a wizard on top of that would put two different "Back"s on the same modal.
  const [method, setMethod] = useState<CreateMethod>(section === 'demographics' ? 'manual' : 'upload');
  const [onInsuranceStep, setOnInsuranceStep] = useState(false);
  // Required fields the person has been in and come back out of without filling. Blur rather than
  // change, so the message arrives when they have finished with a field, not while they type in it.
  const [leftEmpty, setLeftEmpty] = useState<Set<string>>(new Set());
  const noteIfEmpty = (name: string, value: unknown) =>
    setLeftEmpty((prev) => {
      const filled = value instanceof Date ? true : Boolean(String(value ?? '').trim());
      if (filled === !prev.has(name)) return prev;

      const next = new Set(prev);
      if (filled) next.delete(name);
      else next.add(name);
      return next;
    });
  const [coverageEntries, setCoverageEntries] = useState<CoverageEntry[]>(emptyCoverageEntries);

  // Editing insurance on an existing referral edits each COVERAGE, not the referral. Seeded from
  // each coverage's own slot so the form shows what that coverage actually holds, and diffed on save
  // so an untouched coverage is never written.
  const coverageFacets = useMemo(() => workflow?.coverageFacets ?? [], [workflow]);
  const storedFacetValues = useMemo(
    () =>
      Object.fromEntries(
        coverageFacets.map((facet) => [
          facet.id,
          {
            payerName: String(workflow?.valueFor(VALUE_NAMES.PAYER_NAME, { facetId: facet.id })?.value ?? ''),
            memberId: String(workflow?.valueFor(VALUE_NAMES.MEMBER_ID, { facetId: facet.id })?.value ?? ''),
          },
        ])
      ),
    [coverageFacets, workflow]
  );
  const [facetEdits, setFacetEdits] = useState<Record<string, CoverageEntry>>(storedFacetValues);

  // Only coverages whose payer or member ID actually changed — an untouched one is never written.
  const changedFacets = () =>
    coverageFacets
      .map((facet) => ({ facet, edit: facetEdits[facet.id], stored: storedFacetValues[facet.id] }))
      .filter(
        ({ edit, stored }) =>
          edit && stored && (edit.payerName !== stored.payerName || edit.memberId !== stored.memberId)
      );

  // The shared hook rather than this component's own query (master, OLIO-2869): referral creation,
  // referral editing and portal-retrieval entry all offer exactly this set, so they share one cache
  // entry instead of fetching the same list under three names.
  const { facilities: groups, isPending: groupsLoading } = useAllowedFacilities();
  // With access to exactly one building there's nothing to choose — assume it and hide the
  // selector. The selector only appears when a user can address more than one building.
  const onlyBuilding = !groupsLoading && groups.length === 1 ? groups[0] : null;

  // The buildings this referral is addressed to (the sole allowed one, or the multiselect's picks)
  // and their ids. `groupIds` is the submit payload's group list and the source list for the provider
  // prefill below — one derivation so the two can't drift.
  const effectiveBuildings = onlyBuilding ? [{ id: onlyBuilding.id, name: onlyBuilding.name }] : selectedBuildings;
  const groupIds = effectiveBuildings.map((building) => building.id);

  // The facility whose NPI + name seed the provider fields (OLIO-2648). Prefer the first selected
  // facility that actually has an NPI — that's the value the benefit verification needs, and not every
  // facility carries one — and fall back to the first so the organization still fills when none do.
  // Both provider fields come from this one facility, so they always describe the same place. See the
  // seeding effect below (gated on the insurance step, once onInsurance is known).
  const sourceFacilityId =
    groupIds.find((id) => groups.find((group) => group.id === id)?.npiNumber) ?? groupIds[0] ?? null;
  const autoSeededProvider = useRef({ npi: '', org: '' });

  const queryClient = useQueryClient();

  // Apply the settled response to the cache so the acting user sees their own change
  // immediately, rather than waiting for the next list/detail poll to reconcile it.
  const cacheWorkflow = (saved: Workflow) => {
    queryClient.setQueryData(workflowQueryKeys.show(saved.id), saved);
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.listPrefix });
  };

  const { mutate: create, isPending: creating } = useMutateWithToast({
    mutationFn: createWorkflow,
    onSuccess: (created: Workflow) => {
      cacheWorkflow(created);
      onClose();
      navigate(`/inbound-referrals/${created.id}`);
    },
  });
  // Each changed coverage is its own PATCH — the API has no bulk coverage write, and one failing
  // shouldn't silently take the others with it.
  const { mutateAsync: saveCoverage, isPending: savingCoverages } = useMutateWithToast({
    mutationFn: updateFacet,
    onSuccess: (updated: Workflow) => cacheWorkflow(updated),
  });

  const { mutate: save, isPending: saving } = useMutateWithToast({
    mutationFn: updateWorkflow,
    onSuccess: (updated: Workflow) => {
      cacheWorkflow(updated);
      onClose();
    },
  });

  const changedValues = (): WorkflowValuesInput => {
    const changes: WorkflowValuesInput = {};
    TEXT_FIELDS.forEach((field) => {
      const next = text[field.name].trim();
      if (next !== stored.text[field.name]) changes[field.name] = next;
    });
    const nextDob = simpleDashDate(dob) ?? '';
    if (nextDob !== stored.dob) changes[VALUE_NAMES.PATIENT_DOB] = nextDob;
    return changes;
  };

  // What the insurance step typed, as values. `coverages` is the whole typed set, which is the
  // shape extraction produces and the shape per-coverage checks will be derived from (OLIO-2793).
  //
  // Writing `coverages` is what makes each typed insurance its own coverage: the API mints one
  // per entry from this value and copies that entry's payer name and member id into the coverage's
  // own slot, where its payer search and verification read them (OLIO-2793). So a typed member id
  // reaches the coverage it was entered for without this form knowing facets exist.
  //
  // Neither the payer nor the member ID is copied to a referral-level row. Both belong to an
  // insurance, so a copy on the referral is read by nothing and goes stale the moment someone edits
  // the insurance. The list's payer column reads the primary insurance's own payer (OLIO-2890).
  //
  // The trap: a typed set is a MANUAL write of the whole `coverages` list, and manual outranks
  // machine, so alongside a packet upload extraction's coverages would lose to this set rather than
  // merging into it — someone who typed only a primary would discard a secondary the packet named.
  //
  // Two things keep them apart. The methods are exclusive, so switching clears the other side's
  // input — and the `uploads` check below is the backstop that states the invariant where the data
  // is assembled rather than trusting the UI wiring to enforce it. That backstop matters: this hole
  // has been found twice, once in each direction (manual→upload, then upload→manual), because each
  // fix guarded a route instead of the rule.
  const insuranceValues = (): WorkflowValuesInput => {
    if (uploads.length > 0) return {};

    const coverages = coveragesFromEntries(coverageEntries);
    if (coverages.length === 0) return {};

    return { [COVERAGES_VALUE]: coverages };
  };

  const groupsChanged = isEdit && !sameIds(groupIds, originalGroupIds);
  const demographicChanges = changedValues();
  // Not gated on the visible panel: switching tabs hides a field, it doesn't discard what was
  // typed into it. Edit mode has its own insurance section and never reaches this.
  const pendingValues: WorkflowValuesInput = { ...demographicChanges, ...(isEdit ? {} : insuranceValues()) };
  const hasContent = Object.keys(pendingValues).length > 0 || packet.changed || changedFacets().length > 0;
  // A half-entered SSN blocks both Next and the save, and says so under the field. Nine digits or
  // none: a partial number saves cleanly and then matches nothing, which is worse than being asked
  // to finish it. Clearing the field is always a way out, since the number is optional.
  const ssnPartial = ssnEdited && isPartialSsn(text[VALUE_NAMES.PATIENT_SSN]);

  // A field the referral HAD and this edit would empty (OLIO-3000). Blanking one used to save:
  // the API stored "" and read it as supplied, so the checks that need a name activated on
  // nothing and sat there. The API now records a blank as cleared, and this says so before the
  // save rather than after. Only fields that already had a value — a referral whose packet never
  // stated a sex shouldn't be held hostage to it while someone fixes a spelling.
  const clearedFields = DEMOGRAPHIC_FIELDS.filter(
    (field) => !field.optional && stored.text[field.name]?.trim() && !text[field.name]?.trim()
  );
  const clearedNames = new Set(clearedFields.map((field) => field.name));

  const typingReferral = !isEdit && method === 'manual';

  // The typed-referral path only. The form marks optional fields "(optional)"; the rest were only
  // presentationally required — Next opened as soon as anything at all had been typed, so a
  // referral could be created with no last name, sex, or date of birth. Not the upload path: there
  // the AI reads the packet and no demographic field is on screen to fill. And not edit, which
  // keeps its narrower rule above — it blocks EMPTYING a required field, but doesn't hold a
  // referral that arrived without one hostage until someone fills it in.
  const missingRequired = !typingReferral
    ? []
    : DEMOGRAPHIC_FIELDS.filter((field) => {
        if (field.optional) return false;
        if (field.name === VALUE_NAMES.PATIENT_DOB) return !dob;
        return !text[field.name]?.trim();
      });
  // Only about fields they have actually been in and left empty. Keyed on leaving the field rather
  // than on the form having been started: typing a first name says nothing about whether someone
  // intends to fill in the last name, and lighting up every other field the moment they type one
  // character is nagging on a form they are still working down.
  const startedForm = Object.keys(demographicChanges).length > 0;
  const missingNames = new Set(missingRequired.map((field) => field.name).filter((name) => leftEmpty.has(name)));
  // Create only. On edit the two coverages already exist as separate records and each gets its own
  // PATCH, so making them identical there doesn't merge them — the warning would be untrue.
  const duplicateInsurance = !isEdit && duplicatesOneInsurance(coverageEntries);
  const canSubmit =
    !ssnPartial &&
    clearedFields.length === 0 &&
    groupIds.length > 0 &&
    (isEdit ? hasContent || groupsChanged : hasContent && missingRequired.length === 0);

  // Ask the referral, not the deploy. A step still asking for the member ID on the referral itself
  // (no insurance attached to it) means this referral predates per-insurance checks — and for those,
  // the field is the only way to supply one. Newer referrals ask per insurance, so it stays gone.
  const needsReferralLevelMemberId = (workflow?.actions ?? []).some(
    (action) => action.facetId === null && action.required.includes(VALUE_NAMES.MEMBER_ID)
  );
  // Member ID first, then the provider pair. It used to be spliced in after the referral-level
  // payer; with that field gone (OLIO-2890) splicing at the same index would land it between
  // Provider NPI and Provider Organization, splitting a pair that belongs together.
  const insuranceFields = needsReferralLevelMemberId
    ? [REFERRAL_LEVEL_MEMBER_ID_FIELD, ...PROVIDER_FIELDS]
    : PROVIDER_FIELDS;

  // Same rule as the member ID above, same reason: ask the referral, not the deploy. A run is
  // pinned to the definition version it started on, so one already in flight when this shipped
  // doesn't declare `referral_source` at all. Offering the picker there would take an answer, save
  // it happily, and have it apply to nothing — 200, no error, silently never read. CREATE always
  // builds on the published definition, so it always offers it.
  const editOffersReferralSource = Boolean(workflow?.definition?.catalogEntry(VALUE_NAMES.REFERRAL_SOURCE));
  const editDetailFields = editOffersReferralSource ? [REFERRAL_SOURCE_FIELD, ...insuranceFields] : insuranceFields;

  // The link that opened the modal decides what it edits (UX, 2026-08-05). "Add or remove documents"
  // gets the packet alone; "Edit demographics" gets the two-step wizard. One modal rendering
  // everything at once was the complaint — a coordinator clicking one link got both.
  const editingDocuments = isEdit && section === 'documents';
  const editingDemographics = isEdit && section === 'demographics';
  const editingFacilities = isEdit && section === 'facilities';
  // Both the create-by-typing path and edit-demographics run the same two steps.
  const onWizard = typingReferral || editingDemographics;
  const onInsurance = onWizard && onInsuranceStep;

  // Prepopulate Provider NPI + Organization from the referral's facility so a benefit verification can
  // start without hand-entry (OLIO-2648). Both come from the same source facility, so the two fields
  // always describe the same place — NPI from the facility's location, organization from its own name.
  // Gated on the insurance step (where these fields live): seeding earlier would count as "content" in
  // hasContent and enable Submit/Next before the coordinator has provided a packet or demographics.
  // Runs on BOTH create and the Edit Demographics insurance step: an existing referral whose Provider
  // NPI/Organization are empty (its benefit verification can't start without them) gets them seeded
  // from its facility too — the Edit screen was missed the first time. `keepUnlessSeeded` overwrites a
  // field only while it still holds the last auto-seed (its initial empty, or a prior seed), so a
  // stored provider value or a manual edit is never clobbered — and changing the selected facility
  // re-seeds an un-edited field to the new one. `groups` is a stable reference (see
  // useAllowedFacilities), so this runs on real facility/selection changes, not every render.
  useEffect(() => {
    if (!onInsurance || !sourceFacilityId) return;
    const source = groups.find((group) => group.id === sourceFacilityId);
    if (!source) return;
    // On EDIT, seed only from a facility that actually carries an NPI. The NPI is the anchor a benefit
    // verification needs; a bare organization name can't start one. `sourceFacilityId` prefers an
    // NPI-bearing facility but falls back to the first selected one, so without this guard an existing
    // referral whose facility has no NPI would get a lone org auto-written onto an unrelated demographic
    // correction — a provider the coordinator never chose. On CREATE the facility's org legitimately
    // describes the brand-new referral, so it prefills there either way.
    if (isEdit && !source.npiNumber) return;
    const nextNpi = source.npiNumber ?? '';
    const nextOrg = source.name ?? '';
    // Capture the prior seeded values BEFORE the state update: the updater runs during a later render,
    // so reading the ref inside it would see the value we advance here and mistake the seed for a
    // manual edit. `?? ''` because a field missing from the initial `text` record reads as undefined.
    const prevSeeded = autoSeededProvider.current;
    autoSeededProvider.current = { npi: nextNpi, org: nextOrg };
    const keepUnlessSeeded = (held: string | undefined, wasSeeded: string, next: string) =>
      (held ?? '') === wasSeeded ? next : (held ?? '');
    setText((prev) => ({
      ...prev,
      [VALUE_NAMES.PROVIDER_NPI]: keepUnlessSeeded(prev[VALUE_NAMES.PROVIDER_NPI], prevSeeded.npi, nextNpi),
      [VALUE_NAMES.PROVIDER_ORGANIZATION_NAME]: keepUnlessSeeded(
        prev[VALUE_NAMES.PROVIDER_ORGANIZATION_NAME],
        prevSeeded.org,
        nextOrg
      ),
    }));
  }, [onInsurance, sourceFacilityId, groups, isEdit, setText]);
  const showDocuments = isEdit ? editingDocuments : !typingReferral;
  // On create the selector only earns its place when there's a choice to make; in the facilities
  // modal it IS the modal.
  const showFacilitySelect = editingFacilities ? groups.length > 0 : !isEdit && groups.length > 1;
  // On CREATE, Next is a gate: insurance is optional, but there has to be a patient before there's
  // anything to insure. On EDIT it's just navigation between two panels of data that already exists
  // — refusing to move because nothing has changed yet would stop someone going to look at the
  // insurance at all. What's gated on edit is Update, not Next.
  const canAdvance =
    !ssnPartial && clearedFields.length === 0 && (isEdit || (groupIds.length > 0 && missingRequired.length === 0));

  // The steps this save would ripple into (design §9): the changed value names — a
  // packet upload is a change to the `attachments` value — walked through the declared
  // contracts. Empty on create and on terminal runs (the API rejects restarts there).
  const impactedActions = (): WorkflowAction[] => {
    if (!isEdit || workflow.isTerminal) return [];
    const changedNames = Object.keys(pendingValues);
    if (packet.changed) changedNames.push(ATTACHMENTS_VALUE_NAME);
    const fromValues = changedNames.length > 0 ? workflow.impactedActionsBy(changedNames) : [];

    // A coverage correction re-runs that coverage's completed payer search and verification, so it
    // belongs in the consent list too — scoped to the coverage edited, so fixing one doesn't ask to
    // re-run the other's paid check.
    const fromCoverages = changedFacets().flatMap(({ facet }) =>
      workflow.impactedActionsBy([VALUE_NAMES.PAYER_NAME, VALUE_NAMES.MEMBER_ID], [facet.id])
    );

    return [...fromValues, ...fromCoverages].filter(
      (action, index, all) => all.findIndex((other) => other.id === action.id) === index
    );
  };

  const saveEdit = async (restartActionIds?: string[]) => {
    if (!workflow) return;

    // Coverages first, and awaited: they're separate PATCHes, and the referral write is what
    // triggers the restart cascade — letting that land before the coverage values are in would
    // re-run each coverage's check against the payer it was about to stop having.
    // Only the fields that changed, and `null` — never `''` — to clear one. The API stores an empty
    // string verbatim and reads it as PRESENT, so sending '' for an untouched member ID would
    // discharge that coverage's "needs a member ID" flag and dispatch the 270 with nothing in it.
    try {
      for (const { facet, edit, stored } of changedFacets()) {
        await saveCoverage({ workflowId: workflow.id, facetId: facet.id, values: coverageChanges(edit, stored) });
      }
    } catch {
      // The mutation already raised its toast — stop rather than writing the referral on top of a
      // coverage that didn't land, and rather than an unhandled rejection mid-loop.
      return;
    }

    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    if (!values && !packet.changed && !groupsChanged && !restartActionIds) return onClose();

    save({
      workflowId: workflow.id,
      groupIds: groupsChanged ? groupIds : undefined,
      values,
      attachments: packet.entries,
      restartActionIds,
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    if (isEdit) {
      // Consent-at-save (design §9, 002 §6): when the correction ripples into steps
      // that already ran, the person approves the restart list before anything saves.
      const impacted = impactedActions();
      if (impacted.length > 0) {
        setPendingRestart(impacted);
        return;
      }
      saveEdit();
      return;
    }
    const values = Object.keys(pendingValues).length > 0 ? pendingValues : undefined;
    // Uploads only — a referral being created holds no documents to keep.
    create({
      workflowDefinitionApiName: REFERRAL_INTAKE_API_NAME,
      groupIds,
      values,
      attachments: uploads.length > 0 ? uploads : undefined,
    });
  };

  const submitting = creating || saving || savingCoverages;

  // A changed field sends its new value; a cleared one sends null (the API's tombstone). An
  // untouched field is omitted entirely, so it is never rewritten.
  const coverageChanges = (edit: CoverageEntry, stored: CoverageEntry): WorkflowValuesInput => {
    const changes: WorkflowValuesInput = {};
    (
      [
        [VALUE_NAMES.PAYER_NAME, 'payerName'],
        [VALUE_NAMES.MEMBER_ID, 'memberId'],
      ] as [string, keyof CoverageEntry][]
    ).forEach(([name, key]) => {
      if (edit[key] === stored[key]) return;
      changes[name] = edit[key].trim() === '' ? null : edit[key].trim();
    });
    return changes;
  };

  const updateFacetEdit = (facetId: string, key: keyof CoverageEntry, value: string) =>
    setFacetEdits((prev) => ({
      ...prev,
      [facetId]: { ...(prev[facetId] ?? { payerName: '', memberId: '' }), [key]: value },
    }));

  // "Primary Insurance" / "Secondary Insurance". The coverage's own label when the API gave it one,
  // otherwise its position — never invented from the payer, which would rename itself on a typo fix.
  const coverageHeading = (facet: { label: string | null }, index: number) =>
    `${startCase(facet.label ?? COVERAGE_SEQUENCES[index] ?? `coverage ${index + 1}`)} Insurance`;

  const updateCoverage = (index: number, key: keyof CoverageEntry, value: string) =>
    setCoverageEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [key]: value } : entry)));

  // The client's own referral sources, browsed and searched like every other dropdown here: the
  // hook deliberately doesn't fetch, and Select's onMenuOpen pulls the first page, then typing
  // narrows and scrolling pages. Deduplicated by display name server-side, since several raw
  // wordings share one reporting name and a picker must offer it once.
  const referralSources = useInfiniteAttrValues({
    workflowValueName: VALUE_NAMES.REFERRAL_SOURCE,
    distinctDisplayName: true,
    active: true,
    visible: true,
  });

  // The field shows the name the referral RESOLVED to, which is the name reporting counts it under
  // and the name the list column shows. A packet writes its own wording ("Saint Marys") while the
  // picker offers the reporting names that wording resolves to ("St Marys"), so selecting the
  // wording made one referral read as two different sources on two screens, and left picking the
  // reporting name as the only way to make them agree, which throws away what the packet said
  // (OLIO-3158, QA Sep 2).
  //
  // Only while nobody has touched the field. Once someone picks, the field shows their choice.
  const capturedSource = stored.text[VALUE_NAMES.REFERRAL_SOURCE];
  const storedSource = text[VALUE_NAMES.REFERRAL_SOURCE];
  const resolvedSource = workflow?.resolvedAttrValue(VALUE_NAMES.REFERRAL_SOURCE);
  const shownSource = storedSource === capturedSource && resolvedSource ? resolvedSource : storedSource;

  // Kept selectable, because the shown name may not be one the picker offers: an unresolved wording
  // is in no page of the list, and a resolved name only appears once the first page loads.
  // Prepended rather than merged so it can't be paged away.
  const shownSourceOption = useMemo(
    () => (shownSource ? ({ id: shownSource, name: shownSource } as AttrValue) : null),
    [shownSource]
  );

  // The wording it arrived with is deliberately NOT shown alongside the resolved name yet: what to
  // say, and whether to say it here at all, is product's call (asked Sep 3). The extraction card
  // above still states it, so nothing is hidden in the meantime.

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        {field.optional && <Optional> (optional)</Optional>}
      </FieldLabel>
      {field.type === 'date' && (
        <DatePicker
          selected={dob}
          onChange={setDob}
          onBlur={() => noteIfEmpty(field.name, dob)}
          $fullWidth
          placeholder='MM/DD/YYYY'
        />
      )}
      {field.type === 'select' && field.name === VALUE_NAMES.REFERRAL_SOURCE && (
        <div data-testid={`field-${field.name}`}>
          {/* Clearable because a referral is allowed to have no source: the field is optional here,
              nothing in the engine requires one, and AC10 makes "never captured a source" a valid
              state. Without this a coordinator who sees a wrong source can only re-tag it to some
              OTHER real name, which forces a wrong answer into reporting rather than removing it.
              An empty string reaches WriteValues as a tombstone (normalize_blank), which drops the
              referral's attribution and parks the resolve step rather than asking about a blank. */}
          <Select<AttrValue>
            {...referralSources}
            inputId={`field-${field.name}`}
            placeholder='Select'
            isClearable
            styles={REFERRAL_SOURCE_SELECT_STYLES}
            prependedOptions={shownSourceOption ? [shownSourceOption] : []}
            value={shownSourceOption}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.name ?? '' }))}
            getOptionValue={(option) => option.name}
            getOptionLabel={(option) => option.name}
          />
        </div>
      )}
      {field.type === 'select' && field.name !== VALUE_NAMES.REFERRAL_SOURCE && (
        <div data-testid={`field-${field.name}`}>
          <Select
            inputId={`field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            onBlur={() => noteIfEmpty(field.name, text[field.name])}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          onBlur={(e) => noteIfEmpty(field.name, e.target.value)}
          $hasError={clearedNames.has(field.name) || missingNames.has(field.name)}
          placeholder={field.label}
          data-testid={`field-${field.name}`}
        />
      )}
      {clearedNames.has(field.name) && (
        <FieldRequired data-testid={`field-${field.name}-required`}>
          {field.label} can&rsquo;t be removed — the checks on this referral need it.
        </FieldRequired>
      )}
      {missingNames.has(field.name) && (
        <FieldRequired data-testid={`field-${field.name}-required`}>{field.label} is required.</FieldRequired>
      )}
      {field.type === 'ssn' && (
        <SsnField
          digits={text[field.name]}
          // The raw stored value, not the normalised digits: a packet partial is shown exactly as
          // the packet stated it, and the X's are the part worth keeping.
          storedValue={String(workflow?.valueFor(field.name)?.value ?? '')}
          edited={ssnEdited}
          invalid={ssnPartial}
          onChange={(digits) => {
            setSsnEdited(true);
            setText((prev) => ({ ...prev, [field.name]: digits }));
          }}
          // Replacing starts from empty rather than from the packet's digits: the field is for a
          // whole number, and typing around a fragment of unknown position is what would build a
          // wrong one.
          onStartEntry={() => {
            setSsnEdited(true);
            setText((prev) => ({ ...prev, [field.name]: '' }));
          }}
          testId={`field-${field.name}`}
        />
      )}
    </Field>
  );

  // Two-step wizard, one modal (no modal-on-modal): step 1 is the edit form; when the
  // save ripples into started steps, step 2 swaps the modal's content to the restart
  // consent. The form stays mounted (hidden) so Back restores the person's edits —
  // including the uncontrolled file input's queued uploads.
  // Named for what the link promised (UX, 2026-08-05) — a modal titled "Edit Referral" after
  // clicking "Add or remove documents" is what made the two feel like one screen.
  const title = pendingRestart
    ? 'Restart affected steps?'
    : editingDocuments
      ? 'Add or Remove Documents'
      : editingDemographics
        ? 'Edit Demographics'
        : editingFacilities
          ? 'Edit Associated Facilities'
          : 'Create New Referral';

  // Width has to be passed explicitly: StandardModal's default is flex-basis 480px with 560px only
  // as a max, and `flex: 0` means it never grows into that max — so omitting this renders 480px.
  // 640px rather than the mock's 558: at 558 the insurance step is two coverages plus the referral
  // details stacked in one column, which scrolls. The extra width puts each pair side by side
  // (min-width 200px + 16px gap fits twice over) and takes the scroll out.
  return (
    <StandardModal title={title} onCancel={onClose} maxWidth='640px'>
      {pendingRestart && (
        <RestartConsentStep
          actions={pendingRestart}
          saving={saving}
          onConfirm={() => saveEdit(pendingRestart.map((action) => action.id))}
          onBack={() => setPendingRestart(null)}
        />
      )}
      <Form $hidden={!!pendingRestart}>
        {/* Method first, then the facilities it applies to (UX, 2026-08-04). Hidden once the
            insurance step is showing: the method is settled by then, and Back is the way out of
            the step. Leaving it up would offer a second, unlabelled way to leave. */}
        {!isEdit && !onInsurance && (
          <div>
            <MethodLabel>Select a method for creating a new referral</MethodLabel>
            <MethodToggle>
              <SegmentedControl
                options={[
                  { value: 'upload', label: 'Upload Referral Documents', testId: 'method-upload' },
                  { value: 'manual', label: 'Manually Enter Information', testId: 'method-manual' },
                ]}
                selectedValue={method}
                onChange={(value) => {
                  const next = value as CreateMethod;
                  if (next === method) return;
                  setMethod(next);
                  setOnInsuranceStep(false);
                  // The methods are alternatives, so each switch drops the other side's input:
                  // a packet has no place on the typing path, and typed coverages have no place
                  // alongside a packet (they'd outrank everything extraction finds). Both
                  // directions, because guarding only one is what let this through twice.
                  //
                  // Demographics deliberately survive either way: they're scalars, so a manual
                  // one overrides only itself, and typing a name alongside an upload has always
                  // been supported.
                  setCoverageEntries(emptyCoverageEntries);
                  packet.reset();
                }}
                fullWidth
              />
            </MethodToggle>
          </div>
        )}

        {/* Buildings are edited in one place. On create they're part of addressing the referral; on
            an existing one they're the Referral Sent To card's own Edit link, which opens this modal
            on the 'facilities' section. Leaving the selector on the documents and demographics
            screens meant three places wrote the same thing, and it sat on both wizard steps.
            The facilities modal shows it even with a single option — removing the last building is a
            real (if unusual) edit, and Update stays disabled while none is selected. */}
        {/* Outside the selector's condition on purpose: the warning is about what this screen does,
            so it can't wait on the options query to settle before it says it. */}
        {editingFacilities && (
          <Lead data-testid='facilities-lead'>
            Add or remove which facilities are associated to the referral.{' '}
            <Emphasis>Removing a facility from the referral will remove their access to this referral.</Emphasis>
          </Lead>
        )}
        {showFacilitySelect && (
          <Field>
            <div data-testid='building-select'>
              <MultiSelectChips<BuildingOption>
                options={groups}
                values={selectedBuildings}
                onChange={setSelectedBuildings}
                getOptionValue={(building) => building.id}
                getOptionLabel={(building) => building.name}
                title='Select which facilities to associate to this referral'
                placeholder='Select'
              />
            </div>
            {!isEdit && groupIds.length === 0 && startedForm && (
              <FieldRequired data-testid='building-select-required'>At least one facility is required.</FieldRequired>
            )}
          </Field>
        )}
        {!groupsLoading && groups.length === 0 && (
          <HelpText>You don’t have access to any buildings for inbound referrals.</HelpText>
        )}

        {/* Unmounted, not merely hidden, whenever the packet isn't what's being edited. The file
            input is uncontrolled, so hiding it would leave a queued file listed inside a component
            whose state we just cleared — the screen would disagree with what gets sent. On edit this
            is also what keeps the documents link and the demographics link separate screens. */}
        {showDocuments && (
          <Panel>
            <Lead>
              <Emphasis>Upload any associated referral files below.</Emphasis> Olio will process and extract patient
              demographics and related referral information.
            </Lead>
            <PacketEditor packet={packet} />
          </Panel>
        )}

        {/* Not on the facilities screen: adding or removing a building doesn't re-run the steps that
            already ran — what it changes is who can see the referral, which its own copy says. */}
        {isEdit && !editingFacilities && (
          <Alert data-testid='rerun-note'>
            <Alert.Text>
              {editingDocuments
                ? 'Adding or removing files will re-run all completed steps.'
                : 'Editing patient demographics or insurance information will re-run all completed steps.'}
            </Alert.Text>
          </Alert>
        )}

        {editingDemographics && (
          <div data-testid='demographics-section'>
            <Panel $hidden={onInsuranceStep} data-testid='demographics-step'>
              <SectionTitle>Patient Demographics</SectionTitle>
              <Fields>{DEMOGRAPHIC_FIELDS.map(renderField)}</Fields>
            </Panel>
            <Panel $hidden={!onInsuranceStep} data-testid='insurance-step'>
              {coverageFacets.map((facet, index) => (
                <div key={facet.id}>
                  <SectionTitle>{coverageHeading(facet, index)}</SectionTitle>
                  <Fields>
                    {(
                      [
                        ['payerName', 'Payer Name', 'payer-name'],
                        ['memberId', 'Member ID', 'member-id'],
                      ] as [keyof CoverageEntry, string, string][]
                    ).map(([key, label, testId]) => (
                      <Field key={key}>
                        <FieldLabel>
                          {label}
                          <Optional> (optional)</Optional>
                        </FieldLabel>
                        <Input
                          value={facetEdits[facet.id]?.[key] ?? ''}
                          onChange={(e) => updateFacetEdit(facet.id, key, e.target.value)}
                          placeholder={label}
                          data-testid={`facet-${index}-${testId}`}
                        />
                      </Field>
                    ))}
                  </Fields>
                </div>
              ))}

              {/* Always shown, alongside any coverages rather than instead of them. The rendering
                  provider and the referral's plan type belong to the REFERRAL, and the verification
                  requires the provider pair with nothing in the graph producing it — so this form is
                  the only way in. Rendering coverages *instead of* these took Provider NPI and
                  Organization off the screen entirely and left the check unable to start. */}
              {coverageFacets.length > 0 && <SectionTitle>Referral Details</SectionTitle>}
              <Fields>{editDetailFields.map(renderField)}</Fields>
            </Panel>
          </div>
        )}

        {!isEdit && (
          <>
            <Panel $hidden={!typingReferral || onInsuranceStep} data-testid='demographics-step'>
              <SectionTitle>Patient Demographics</SectionTitle>
              <Fields>{DEMOGRAPHIC_FIELDS.map(renderField)}</Fields>
            </Panel>

            <Panel $hidden={!onInsurance} data-testid='insurance-step'>
              {/* One sentence, plural: the secondary really does get its own check now, so the note
                  that used to explain it wouldn't isn't just redundant, it's wrong (OLIO-2793). */}
              <Lead>
                If available, provide the patient’s insurance information below. Olio will run a benefit verification
                for each insurance you provide.
              </Lead>
              {COVERAGE_SEQUENCES.map((sequence, index) => (
                <div key={sequence}>
                  <SectionTitle>{startCase(sequence)} Insurance</SectionTitle>
                  <Fields>
                    {(
                      [
                        ['payerName', 'Payer Name', 'payer-name'],
                        ['memberId', 'Member ID', 'member-id'],
                      ] as [keyof CoverageEntry, string, string][]
                    ).map(([key, label, testId]) => (
                      <Field key={key}>
                        <FieldLabel>
                          {label}
                          <Optional> (optional)</Optional>
                        </FieldLabel>
                        <Input
                          value={coverageEntries[index][key]}
                          onChange={(e) => updateCoverage(index, key, e.target.value)}
                          placeholder={label}
                          data-testid={`coverage-${sequence}-${testId}`}
                        />
                      </Field>
                    ))}
                  </Fields>
                  {/* Asks rather than refuses: typing the same insurance twice is usually a mistake,
                      but it's the person's to make, and the one coverage they end up with is a
                      correct reading of what they typed. Without this the second row just
                      disappeared with nothing said. Copy from UX, 2026-08-10. */}
                  {sequence === 'secondary' && duplicateInsurance && (
                    <DuplicateWarning data-testid='duplicate-coverage-note'>
                      <WarningBannerText>
                        The primary and secondary insurance provided match. Ensure this is correct before submitting.
                      </WarningBannerText>
                    </DuplicateWarning>
                  )}
                </div>
              ))}

              {/* The rendering provider, asked for here as well as on edit. Nothing in the graph
                  produces these two and the verification requires them, so a referral created
                  without them starts with every Benefit Verification flagged and needs an edit
                  before any check can run. plan_type isn't offered — its label is "Payer Name",
                  which each insurance above already has, and create derives it from the primary. */}
              <SectionTitle>Referral Details</SectionTitle>
              <Fields>{[REFERRAL_SOURCE_FIELD, ...PROVIDER_FIELDS].map(renderField)}</Fields>
            </Panel>
          </>
        )}

        <Actions>
          {onInsurance ? (
            <Button variant='ghost' onClick={() => setOnInsuranceStep(false)} data-testid='back-to-demographics'>
              Back
            </Button>
          ) : (
            <Button variant='ghost' onClick={onClose} data-testid='cancel-referral'>
              Cancel
            </Button>
          )}
          {onWizard && !onInsuranceStep ? (
            <Button onClick={() => setOnInsuranceStep(true)} disabled={!canAdvance} data-testid='next-step'>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting || packet.isUploading || packet.failed || packet.emptied}
              loading={submitting}
              data-testid='save-referral'>
              {isEdit ? 'Update' : onInsurance ? 'Submit' : 'Create Referral'}
            </Button>
          )}
        </Actions>
      </Form>
    </StandardModal>
  );
}

// 24px between sections and 16px above the first one (the shared modal body already contributes
// 24px of top padding, and the mock wants 40px clear of the header). Measured from Figma
// 10317:3729 rather than eyeballed — 16px between sections read as cramped.
const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
`;

const Panel = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`;

// 14px, matching the mock's `body/normal` (Figma 10317:3729). It was 16px, which is the design's
// `subtitle/normal` size — and that's what made the bolded lead-in on the upload step shout: the
// weight was right (`body/bold` is 700), the text was just two pixels too big for it.
// Inline bold inside body copy. A bare <strong> takes the browser's 700, which is not this brand's
// bold: Special Gothic is variable, and FONT_WEIGHT_MEDIUM (500) is the weight that maps to what Lato
// called bold (UX, 2026-08-10). So an unstyled <strong> renders heavier than every heading on the
// form. Still a <strong> element — the emphasis is real, it just takes the theme's weight.
const Emphasis = styled.strong`
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;

const Lead = styled(BodySmall)`
  display: block;
`;

// Same shared SegmentedControl the Active Referrals list page uses for its status chips —
// its tinted-panel selection style already reads as distinct from the modal's solid submit button.
const MethodToggle = styled.div`
  margin-top: 8px;
`;

// 12px rather than Lead's usual size — a caption for the toggle beneath it, not a paragraph.
const MethodLabel = styled(Lead)`
  font-size: 12px;
`;

const DuplicateWarning = styled(WarningBanner)`
  margin-top: 8px;
`;

const Fields = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Field = styled.div`
  min-width: 200px;
  flex: 1;
`;

// 40px above a section that follows other content inside the same panel (step 2: Primary Insurance
// > Secondary Insurance > Referral Details) — but nothing when it opens the panel, where the form's
// own 24px gap is already the separation and the extra read as a hole. Both forms of "opens the
// panel" are covered: the title directly, and the title inside the first per-coverage block on the
// insurance step.
// BodySmallBold rather than BodySmall + a hand-written weight: the typography component already
// carries FONT_WEIGHT_MEDIUM, which is the brand's mapping of bold onto Special Gothic. Restating
// the number here is how a component drifts from the theme without anyone noticing.
const SectionTitle = styled(BodySmallBold)`
  display: block;
  margin-top: 40px;
  color: ${colors.black100};

  ${Panel} > &:first-child,
  ${Panel} > *:first-child > &:first-child {
    margin-top: 0;
  }
`;

const Optional = styled.span`
  font-weight: 400;
  color: ${colors.black50};
`;

const FieldRequired = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.red100};
`;

const HelpText = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.red100};
`;
